import React, { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";

/** Magnetic button — cursor pulls the button toward it on hover. */
export function MagneticButton({ children, onClick, testId, className = "" }) {
  const ref = useRef(null);
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 300, damping: 20 });
  const sy = useSpring(y, { stiffness: 300, damping: 20 });

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    x.set((e.clientX - r.left - r.width / 2) * 0.35);
    y.set((e.clientY - r.top - r.height / 2) * 0.35);
  };
  const onLeave = () => {
    x.set(0);
    y.set(0);
  };

  return (
    <motion.button
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      onClick={onClick}
      data-testid={testId}
      style={{ x: sx, y: sy, background: "var(--text-primary)", color: "var(--canvas)" }}
      className={`group inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-medium ${className}`}
    >
      {children}
    </motion.button>
  );
}

/** Tilt card — perspective-based 3D rotate on cursor. */
export function TiltCard({ children, testId, className = "" }) {
  const ref = useRef(null);
  const rx = useSpring(0, { stiffness: 200, damping: 18 });
  const ry = useSpring(0, { stiffness: 200, damping: 18 });
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width - 0.5;
    const py = (e.clientY - r.top) / r.height - 0.5;
    ry.set(px * 14);
    rx.set(-py * 14);
  };
  const onLeave = () => {
    rx.set(0);
    ry.set(0);
  };
  return (
    <motion.div
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testId}
      style={{
        rotateX: rx,
        rotateY: ry,
        transformPerspective: 800,
        transformStyle: "preserve-3d",
      }}
      className={className}
    >
      <div style={{ transform: "translateZ(40px)" }}>{children}</div>
    </motion.div>
  );
}

/** Spotlight card — radial light follows cursor. */
export function SpotlightCard({ children, testId, className = "" }) {
  const ref = useRef(null);
  const mx = useMotionValue(-200);
  const my = useMotionValue(-200);
  const bg = useTransform(
    [mx, my],
    ([nx, ny]) =>
      `radial-gradient(220px circle at ${nx}px ${ny}px, rgba(255,255,255,0.10), transparent 60%)`,
  );
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set(e.clientX - r.left);
    my.set(e.clientY - r.top);
  };
  return (
    <div
      ref={ref}
      onMouseMove={onMove}
      data-testid={testId}
      className={`relative overflow-hidden ${className}`}
    >
      <motion.div
        style={{ background: bg }}
        className="pointer-events-none absolute inset-0"
      />
      {children}
    </div>
  );
}

/** Number ticker that animates up to a target when in view. */
export function NumberTicker({ value, suffix = "", duration = 1.4 }) {
  const ref = useRef(null);
  const [n, setN] = React.useState(0);
  React.useEffect(() => {
    let raf;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          const start = performance.now();
          const tick = (t) => {
            const p = Math.min(1, (t - start) / (duration * 1000));
            const eased = 1 - Math.pow(1 - p, 3);
            setN(Math.round(value * eased));
            if (p < 1) raf = requestAnimationFrame(tick);
          };
          raf = requestAnimationFrame(tick);
          io.disconnect();
        }
      },
      { threshold: 0.4 },
    );
    if (ref.current) io.observe(ref.current);
    return () => {
      io.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [value, duration]);
  return (
    <span ref={ref}>
      {n.toLocaleString()}
      {suffix}
    </span>
  );
}

/** Flip card with front/back faces. */
export function FlipCard({ front, back, testId }) {
  const [flipped, setFlipped] = React.useState(false);
  return (
    <div
      data-testid={testId}
      onClick={() => setFlipped((v) => !v)}
      className="relative cursor-pointer w-full h-full"
      style={{ perspective: 1000 }}
    >
      <motion.div
        animate={{ rotateY: flipped ? 180 : 0 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        style={{ transformStyle: "preserve-3d" }}
        className="relative w-full h-full"
      >
        <div
          className="absolute inset-0 rounded-2xl border border-hairline bg-glass backdrop-blur-2xl p-8 flex flex-col justify-between"
          style={{ backfaceVisibility: "hidden" }}
        >
          {front}
        </div>
        <div
          className="absolute inset-0 rounded-2xl border border-hairline bg-surface-2 p-8 flex flex-col justify-between"
          style={{
            backfaceVisibility: "hidden",
            transform: "rotateY(180deg)",
          }}
        >
          {back}
        </div>
      </motion.div>
    </div>
  );
}
